import { useEffect, useState } from "react";
import { Download, Share } from "lucide-react";
import { isNativeApp } from "@/lib/nativeAudio";

const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) &&
  !window.MSStream;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true ||
  isNativeApp();

const InstallPWAButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      setInstalled(true);
      return;
    }
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const onClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") setInstalled(true);
      setDeferredPrompt(null);
    } else if (isIOS()) {
      setShowIOSHint(true);
    }
  };

  // Only render button if we have a prompt available OR we're on iOS (needs manual instruction)
  const canShow = deferredPrompt || isIOS();
  if (!canShow) return null;

  return (
    <>
      <button
        data-testid="pwa-install-btn"
        onClick={onClick}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-[11px] font-bold uppercase tracking-widest transition-colors"
        aria-label="Instalar aplicativo"
      >
        <Download className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Instalar</span>
      </button>

      {showIOSHint && (
        <div
          className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIOSHint(false)}
          data-testid="pwa-ios-hint-modal"
        >
          <div
            className="w-full max-w-sm bg-white rounded-2xl p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-display text-2xl font-extrabold uppercase text-[var(--fi-blue)] leading-tight">
              Instalar no iPhone
            </div>
            <ol className="mt-4 space-y-3 text-sm text-gray-700 list-decimal list-inside">
              <li>
                Toque no botão de compartilhamento{" "}
                <Share className="inline w-4 h-4 -mt-0.5 text-[var(--fi-blue)]" />{" "}
                do Safari.
              </li>
              <li>
                Selecione{" "}
                <span className="font-bold text-[var(--fi-blue)]">
                  Adicionar à Tela de Início
                </span>
                .
              </li>
              <li>Toque em <span className="font-bold">Adicionar</span> no canto superior direito.</li>
            </ol>
            <button
              data-testid="pwa-ios-hint-close"
              onClick={() => setShowIOSHint(false)}
              className="mt-5 w-full rounded-xl bg-[var(--fi-blue)] text-white font-bold uppercase tracking-widest text-sm py-3 hover:bg-[var(--fi-blue-2)] transition-colors"
            >
              Entendi
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWAButton;
